import React, { useEffect } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import AleaInput from '../../components/AleaInput/AleaInput.tsx';
import AleaButtonChoice from '../../components/AleaButtonChoice/AleaButtonChoice.tsx';
import AleaSelect from '../../components/AleaSelect/AleaSelect.tsx';
import { useTranslation } from 'react-i18next';
import getGenderName from '../../utils/getGenderName.ts';
import { getLanguageKeyByType } from '../../utils/getLanguageKeyByType.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import AleaNewLivingAreaModal from '../../modals/AleaNewLivingAreaModal/AleaNewLivingAreaModal.tsx';
import AleaLoader from '../../components/AleaLoader/AleaLoader.tsx';
import StationService from '../../services/StationService.ts';
import ResidentHeadline from './ResidentHeadline';

interface PersonalDataFormValues {
  firstName: string;
  lastName: string;
  weight: string;
  height: string;
  livingArea: string;
  room: string;
  gender: string;
}

interface PersonalDataTabProps {
  stations: { id: string; name: string }[];
  stationOptions: string[];
  onRefreshStations?: () => Promise<void>;
}

const PersonalDataTab: React.FC<PersonalDataTabProps> = ({
  stations,
  stationOptions,
  onRefreshStations,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<PersonalDataFormValues>();

  const [isAddLivingAreaOpen, setIsAddLivingAreaOpen] = React.useState(false);
  const [isStationBusy, setIsStationBusy] = React.useState(false);

  

  useEffect(() => {
    const currentLivingArea = watch('livingArea');

    if (stations.length > 0 && currentLivingArea) {
      const station = stations.find(
        (station) => station.id === currentLivingArea
      );

      if (station && station.name !== currentLivingArea) {
        setValue('livingArea', String(station.name));
        trigger('livingArea');
      }
    }
  }, [stations, watch('livingArea')]);

  return (
    <Flex direction="column" alignItems="center">
      <Flex
        flexFlow="column"
        alignItems="flex-start"
        maxWidth="949px"
        w="100%"
        mb="55px"
      >
        <ResidentHeadline
          title={t('personalData')}
          description={t('promptPersonalData')}
        />
      </Flex>

      <Box maxWidth="425px" mb="20px">
        <Flex mb="30px" direction={{ base: 'column', md: 'row' }}>
          <Box
            flex="1"
            mr={{ base: 0, md: '25px' }}
            mb={{ base: '25px', md: 0 }}
          >
            <AleaInput
              label={t('firstName')}
              value={watch('firstName') || ''}
              onChange={(e) => setValue('firstName', e.target.value)}
              register={register('firstName')}
              error={errors.firstName?.message}
              isRequired
            />
          </Box>
          <Box flex="1">
            <AleaInput
              label={t('lastName')}
              value={watch('lastName') || ''}
              onChange={(e) => setValue('lastName', e.target.value)}
              register={register('lastName')}
              error={errors.lastName?.message}
              isRequired
            />
          </Box>
        </Flex>

        <Flex mb="30px" direction={{ base: 'column', md: 'row' }}>
          <Box
            flex="1"
            mr={{ base: 0, md: '25px' }}
            mb={{ base: '25px', md: 0 }}
          >
            <AleaInput
              label={t('weight')}
              value={watch('weight') || ''}
              onChange={(e) => setValue('weight', e.target.value)}
              register={register('weight')}
              error={errors.weight?.message}
              suffix="kg"
            />
          </Box>
          <Box flex="1">
            <AleaInput
              label={t('height')}
              value={watch('height') || ''}
              onChange={(e) => setValue('height', e.target.value)}
              register={register('height')}
              error={errors.height?.message}
              suffix="cm"
            />
          </Box>
        </Flex>

        <Box mb="30px">
          <AleaButtonChoice
            label={t('gender')}
            items={[t('woman'), t('man'), t('diverse')]}
            value={getGenderName(watch('gender')) || ''}
            onChange={(value) => {
              setValue('gender', value);
              trigger('gender');
            }}
            errorMessage={errors.gender?.message}
            fullWidth
            isRequired
          />
        </Box>

        <Flex mb="30px" direction={{ base: 'column', md: 'row' }}>
          <Box
            flex="1"
            mr={{ base: 0, md: '25px' }}
            mb={{ base: '25px', md: 0 }}
          >
            <AleaSelect
              label={t(getLanguageKeyByType('livingArea', user?.carehome.type))}
              value={watch('livingArea') || ''}
              options={stationOptions}
              onChange={(value) => {
                setValue('livingArea', value);
                trigger('livingArea');
              }}
              error={errors.livingArea?.message}
              isRequired
              enableNewLivingArea
              onNewLivingAreaClick={() => setIsAddLivingAreaOpen(true)}
              newOptionText={t('newLivingArea')}
            />
        <AleaNewLivingAreaModal
          isOpen={isAddLivingAreaOpen}
          onClose={() => setIsAddLivingAreaOpen(false)}
          onConfirm={async (values) => {
            try {
              setIsStationBusy(true);
              const created = await StationService.createStation({
                name: values.livingAreaName,
                carehome_id: user?.carehome?.id || '',
                is_active: true,
                extra_data: null,
                compound_phone_number: values.telephone?.phone
                  ? { code: values.telephone.code, phone: values.telephone.phone }
                  : null,
              } as any);
              setValue('livingArea', created.name);
              trigger('livingArea');
              if (onRefreshStations) {
                await onRefreshStations();
              }
            } finally {
              setIsAddLivingAreaOpen(false);
              setIsStationBusy(false);
            }
          }}
        />
        {isStationBusy && <AleaLoader />}
          </Box>
          <Box flex="1">
            <AleaInput
              label={t('room')}
              value={watch('room') || ''}
              onChange={(e) => setValue('room', e.target.value)}
              register={register('room')}
              error={errors.room?.message}
              isRequired
            />
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
};

export default PersonalDataTab;
